import { useState } from "react"
import styles from "./tournament-form.module.css"


export default function TournamentForm({ onSubmit, loading, errorMessage }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    game: "",
    registrationPrice: 0,
    maxPlayers: "",
    rules: "",
    discordLink: "",
    prizePool: "",
    tournamentDate: "",
    registrationDeadline: "",
    bannerImage: "",
  })

  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})

  const validateField = (name, value) => {
    let error = ""

    switch (name) {
      case "name":
        if (!value || value.trim().length === 0) error = "Tournament name is required"
        else if (value.length < 3) error = "Name must be at least 3 characters"
        else if (value.length > 100) error = "Name must not exceed 100 characters"
        break

      case "description":
        if (!value || value.trim().length === 0) error = "Description is required"
        else if (value.length < 10) error = "Description must be at least 10 characters"
        else if (value.length > 1000) error = "Description must not exceed 1000 characters"
        break

      case "game":
        if (!value || value.trim().length === 0) error = "Please specify a game"
        break

      case "registrationPrice":
        if (value < 0) error = "Registration price cannot be negative"
        break

      case "maxPlayers":
        if (!value) error = "Max players is required"
        else if (value < 2) error = "Tournament must have at least 2 players"
        else if (value > 1000) error = "Tournament cannot exceed 1000 players"
        break

      case "rules":
        if (!value || value.trim().length === 0) error = "Tournament rules are required"
        else if (value.length < 10) error = "Rules must be at least 10 characters"
        else if (value.length > 2000) error = "Rules must not exceed 2000 characters"
        break

      case "discordLink":
        if (!value || value.trim().length === 0) error = "Discord link is required"
        else if (!/^https:\/\/discord\.gg\/[\w\d-]+/.test(value)) error = "Please provide a valid Discord invite link"
        break

      case "tournamentDate":
        if (!value) error = "Tournament date is required"
        else {
          const selectedDate = new Date(value)
          const now = new Date()
          if (selectedDate <= now) error = "Tournament date must be in the future"
        }
        break

      case "registrationDeadline":
        if (!value) error = "Registration deadline is required"
        else {
          const deadline = new Date(value)
          const now = new Date()
          const tournamentDate = new Date(formData.tournamentDate)
          if (deadline <= now) error = "Deadline must be in the future"
          if (deadline >= tournamentDate) error = "Deadline must be before tournament date"
        }
        break

      default:
        break
    }

    return error
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: name === "maxPlayers" || name === "registrationPrice" ? (value ? Number.parseFloat(value) : "") : value,
    }))

    if (touched[name]) {
      const error = validateField(name, value)
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }))
    }
  }

  // const handleBanner = (e) => {
  //   e.preventDefault();
  //   const file = e.target.files[0];
  //   setFormData((prev) => ({
  //     ...prev,
  //     bannerImage: file,
  //   }))
  //   setPreview(URL.createObjectURL(file)); // instant preview
  // };

  const handleBlur = (e) => {
    const { name, value } = e.target
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }))

    const error = validateField(name, value)
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }))
  }

  const validateForm = () => {
    const newErrors = {}
    Object.keys(formData).forEach((key) => {
      if (key !== "prizePool" && key !== "bannerImage") {
        const error = validateField(key, formData[key])
        if (error) newErrors[key] = error
      }
    })
    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const newErrors = validateForm()

    if (Object.keys(newErrors).length === 0) {
      onSubmit(formData);
    } else {
      setErrors(newErrors)
      Object.keys(newErrors).forEach((key) => {
        setTouched((prev) => ({
          ...prev,
          [key]: true,
        }))
      })
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.formSection}>
        <h2 className={styles.sectionTitle}>Basic Information</h2>

        <div className={styles.formGroup}>
          <label htmlFor="name" className={styles.label}>
            Tournament Name <span className={styles.required}>*</span>
          </label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter tournament name (3-100 characters)"
            className={`${styles.input} ${errors.name && touched.name ? styles.error : ""}`}
            maxLength="100"
          />
          {errors.name && touched.name && <span className={styles.errorMessage}>{errors.name}</span>}
          <span className={styles.charCount}>{formData.name.length}/100</span>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description" className={styles.label}>
            Description <span className={styles.required}>*</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Describe your tournament (10-1000 characters)"
            className={`${styles.textarea} ${errors.description && touched.description ? styles.error : ""}`}
            maxLength="1000"
            rows="4"
          />
          {errors.description && touched.description && (
            <span className={styles.errorMessage}>{errors.description}</span>
          )}
          <span className={styles.charCount}>{formData.description.length}/1000</span>
        </div>

        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label htmlFor="game" className={styles.label}>
              Game <span className={styles.required}>*</span>
            </label>
            <input
              id="game"
              type="text"
              name="game"
              value={formData.game}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter game"
              className={`${styles.input} ${errors.name && touched.name ? styles.error : ""}`}
            />
            {errors.game && touched.game && <span className={styles.errorMessage}>{errors.game}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="maxPlayers" className={styles.label}>
              Max Players <span className={styles.required}>*</span>
            </label>
            <input
              id="maxPlayers"
              type="number"
              name="maxPlayers"
              value={formData.maxPlayers}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="2-1000"
              className={`${styles.input} ${errors.maxPlayers && touched.maxPlayers ? styles.error : ""}`}
              min="2"
              max="1000"
            />
            {errors.maxPlayers && touched.maxPlayers && (
              <span className={styles.errorMessage}>{errors.maxPlayers}</span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.formSection}>
        <h2 className={styles.sectionTitle}>Tournament Details</h2>

        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label htmlFor="registrationPrice" className={styles.label}>
              Registration Price ($) <span className={styles.required}>*</span>
            </label>
            <input
              id="registrationPrice"
              type="number"
              name="registrationPrice"
              value={formData.registrationPrice}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="0 for free tournament"
              className={`${styles.input} ${errors.registrationPrice && touched.registrationPrice ? styles.error : ""}`}
              min="0"
              step="0.01"
            />
            {errors.registrationPrice && touched.registrationPrice && (
              <span className={styles.errorMessage}>{errors.registrationPrice}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="prizePool" className={styles.label}>
              Prize Pool (Optional)
            </label>
            <input
              id="prizePool"
              type="text"
              name="prizePool"
              value={formData.prizePool}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., $1000 cash + Gaming Gear"
              className={styles.input}
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="rules" className={styles.label}>
            Tournament Rules <span className={styles.required}>*</span>
          </label>
          <textarea
            id="rules"
            name="rules"
            value={formData.rules}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Specify tournament rules, format, scoring, etc. (10-2000 characters)"
            className={`${styles.textarea} ${errors.rules && touched.rules ? styles.error : ""}`}
            maxLength="2000"
            rows="5"
          />
          {errors.rules && touched.rules && <span className={styles.errorMessage}>{errors.rules}</span>}
          <span className={styles.charCount}>{formData.rules.length}/2000</span>
        </div>
      </div>

      <div className={styles.formSection}>
        <h2 className={styles.sectionTitle}>Dates & Links</h2>

        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label htmlFor="tournamentDate" className={styles.label}>
              Tournament Date <span className={styles.required}>*</span>
            </label>
            <input
              id="tournamentDate"
              type="datetime-local"
              name="tournamentDate"
              value={formData.tournamentDate}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`${styles.input} ${errors.tournamentDate && touched.tournamentDate ? styles.error : ""}`}
            />
            {errors.tournamentDate && touched.tournamentDate && (
              <span className={styles.errorMessage}>{errors.tournamentDate}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="registrationDeadline" className={styles.label}>
              Registration Deadline <span className={styles.required}>*</span>
            </label>
            <input
              id="registrationDeadline"
              type="datetime-local"
              name="registrationDeadline"
              value={formData.registrationDeadline}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`${styles.input} ${errors.registrationDeadline && touched.registrationDeadline ? styles.error : ""}`}
            />
            {errors.registrationDeadline && touched.registrationDeadline && (
              <span className={styles.errorMessage}>{errors.registrationDeadline}</span>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="discordLink" className={styles.label}>
            Discord Invite Link <span className={styles.required}>*</span>
          </label>
          <input
            id="discordLink"
            type="url"
            name="discordLink"
            value={formData.discordLink}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="https://discord.gg/example"
            className={`${styles.input} ${errors.discordLink && touched.discordLink ? styles.error : ""}`}
          />
          {errors.discordLink && touched.discordLink && (
            <span className={styles.errorMessage}>{errors.discordLink}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="bannerImage" className={styles.label}>
            Banner Image URL (Optional)
          </label>
          <input
            id="bannerImage"
            type="text"
            name="bannerImage"
            value={formData.bannerImage}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="https://example.com/banner.jpg"
            className={`${styles.input} ${errors.bannerImage && touched.bannerImage ? styles.error : ""}`}
          />
          {errors.bannerImage && touched.bannerImage && (
            <span className={styles.errorMessage}>{errors.bannerImage}</span>
          )}
          {formData.bannerImage && (
            <div className={styles.imagePreview}>
              <img
                src={formData.bannerImage || "/placeholder.svg"}
                alt="Banner preview"
              />
            </div>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}
      <div className={styles.formActions}>
        <button type="submit" className={`${styles.submitButton} ${loading ? styles.loading : ""}`} disabled={loading}>
          {loading ? "Creating Tournament..." : "Create Tournament"}
        </button>
      </div>
    </form>
  )
}
